import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

import { first } from 'rxjs';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { NgxMasonryModule } from 'ngx-masonry';

import { CreatorService } from '../../shared/services/creator.service';
import { ChatStateService } from '../../shared/services/chat-state.service';
import { StatsigService } from 'app/shared/services/statsig.service';

import { CreatorMediaCardComponent } from '../../components/creator-media-card/creator-media-card.component';
import { SvgIconComponent } from '../../shared/components/svg-icon/svg-icon.component';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { MasonryGalleryComponent } from '../../shared/components/masonry-gallery/masonry-gallery.component';

import { ISvgConfig } from '../../shared/interfaces/svg.interfaces';
import { IButtonConfig } from '../../shared/interfaces/button.interfaces';
import { ICreatorContent, ICreatorData } from '../../interfaces/creator.interfaces';

import { EButtonSizes, EButtonTypes } from '../../shared/enums/button.enums';
import { ESvgTypes } from '../../shared/enums/svg.enums';
import { ECreatorMediaTypes } from '../../enums/creator.enums';
import { EFeatureGates } from 'app/shared/enums/feature-gates.enums';

@Component({
  selector: 'stxt-creator-profile-page',
  standalone: true,
  imports: [
    SvgIconComponent,
    ButtonComponent,
    CommonModule,
    NgxMasonryModule,
    CreatorMediaCardComponent,
    MasonryGalleryComponent
  ],
  templateUrl: './creator-profile-page.component.html',
  styleUrl: './creator-profile-page.component.scss',
  providers: [ReCaptchaV3Service]
})
export class CreatorProfilePageComponent implements OnInit {
  public ageIcon: ISvgConfig = {
    name: 'birthday',
    fill: ESvgTypes.None
  };
  public heightIcon: ISvgConfig = {
    name: 'height',
    fill: ESvgTypes.None
  };
  public ethnicityIcon: ISvgConfig = {
    name: 'ethnicity',
    fill: ESvgTypes.None
  };
  chatIcon: ISvgConfig = {
    name: 'conversation',
    fill: ESvgTypes.None
  };
  public leftIcon: ISvgConfig = {
    name: 'arrow_left',
    fill: ESvgTypes.ColorFilledDarker
  };
  public moreIcon: ISvgConfig = {
    name: 'more',
    fill: ESvgTypes.ColorFilledDarker
  };
  public shareIcon: ISvgConfig = {
    name: 'share',
    fill: ESvgTypes.ColorFilledDarker
  };
  public chatNowButtonConfig: IButtonConfig = {
    fill: EButtonTypes.SecondaryFilled,
    buttonSize: EButtonSizes.Default,
    text: 'Chat now'
  };
  creatorId = this.route.snapshot.paramMap.get('creatorId');
  creatorData: ICreatorData;
  userId: string = localStorage.getItem('userId');
  showChatCTA: boolean = false;
  creatorCardSelector = '.creator_button__desktop';
  user = JSON.parse(localStorage.getItem('user'));
  mediaList: ICreatorContent[] = [];
  protected readonly ECreatorMediaTypes = ECreatorMediaTypes;
  isRecaptchaEnabled: boolean;

  @HostListener('window:scroll', [])
  onWindowScroll() {
    const creatorCard = document.querySelector(this.creatorCardSelector);
    if (creatorCard) {
      const cardRect = creatorCard.getBoundingClientRect();
      const cardTop = cardRect.top;
      const scrollThreshold = 350;
      this.showChatCTA = window.scrollY > cardTop + scrollThreshold;
    }
  }

  constructor(
    public creatorService: CreatorService,
    public router: Router,
    public location: Location,
    public route: ActivatedRoute,
    private reCaptchaV3Service: ReCaptchaV3Service,
    private chatStateService: ChatStateService,
    private statsigService: StatsigService
  ) {}

  ngOnInit(): void {
    this.getCreatorData();
    this.checkStatsigFeatureGate(EFeatureGates.RECAPTCHA);
  }

  getCreatorData(): void {
    this.creatorService
      .getCreatorData(this.creatorId)
      .pipe(first())
      .subscribe((res: ICreatorData) => {
        this.creatorData = res;
        this.mediaList = res.content.filter(
          item => item.type === ECreatorMediaTypes.Image || item.type === ECreatorMediaTypes.Video
        );
      });
  }

  navigateBack(): void {
    this.location.back();
  }

  async checkStatsigFeatureGate(featureGate: EFeatureGates): Promise<void> {
    try {
      await this.statsigService.waitForInitialization();
      this.isRecaptchaEnabled = await this.statsigService.checkFeatureGate(featureGate);
    } catch (error) {
      this.isRecaptchaEnabled = false;
    }
  }

  openChat(): void {
    if (this.isRecaptchaEnabled) {
      this.reCaptchaV3Service
        .execute('chat_with_creator')
        .pipe(first())
        .subscribe(() => {
          this.navigateToChat();
        });
    } else {
      this.navigateToChat();
    }
  }

  navigateToChat(): void {
    localStorage.setItem('backUrl', `/creator/${this.creatorData.id}`);
    this.chatStateService.setPendingCreatorId(this.creatorData.id);
    this.router.navigateByUrl(`chat/${this.creatorData.id}`);
  }
}
